"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";

export type FurnitureOptionalDimensionAxes = {
  length?: boolean;
  width?: boolean;
  height?: boolean;
};

export type FurnitureOptionalDimensionsValue = {
  lengthFt: number | "";
  widthFt: number | "";
  heightFt: number | "";
};

interface FurnitureOptionalDimensionsFieldsProps {
  value: FurnitureOptionalDimensionsValue;
  onChange: (next: FurnitureOptionalDimensionsValue) => void;
  /** Which axes to show (default length + width). */
  axes?: FurnitureOptionalDimensionAxes;
  /** Button / summary label when collapsed. */
  summaryLabel?: string;
  /** Skip the collapse control and always show inputs. */
  alwaysExpanded?: boolean;
  className?: string;
}

function parseOptionalFt(raw: string): number | "" {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const n = Number.parseFloat(trimmed);
  return Number.isFinite(n) ? n : "";
}

/**
 * Collapsed-by-default optional L/W(/H) ft inputs (furniture Wave 2 — D3 copy).
 */
export default function FurnitureOptionalDimensionsFields({
  value,
  onChange,
  axes = { length: true, width: true, height: false },
  summaryLabel = "Table size (optional)",
  alwaysExpanded = false,
  className = "",
}: FurnitureOptionalDimensionsFieldsProps) {
  const hasAnyValue =
    value.lengthFt !== "" || value.widthFt !== "" || value.heightFt !== "";
  const [expanded, setExpanded] = useState(hasAnyValue || alwaysExpanded);

  const showLength = axes.length !== false;
  const showWidth = axes.width === true;
  const showHeight = axes.height === true;
  const showFields = alwaysExpanded || expanded;

  return (
    <div className={cn("space-y-2", className)}>
      {!alwaysExpanded ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex w-full items-center justify-between rounded-lg border border-border bg-surface px-3 py-2 text-left text-sm font-medium text-foreground hover:border-border-strong"
          aria-expanded={expanded}
        >
          <span>{summaryLabel}</span>
          <span className="text-xs font-normal text-text-light">
            {expanded ? "Hide" : "Add"}
          </span>
        </button>
      ) : null}

      {showFields ? (
        <div
          className={cn(
            "space-y-3",
            !alwaysExpanded &&
              "rounded-lg border border-border bg-secondary/40 p-3",
          )}
        >
          <div
            className={cn(
              "grid gap-3",
              showHeight
                ? "grid-cols-1 sm:grid-cols-3"
                : "grid-cols-1 sm:grid-cols-2",
            )}
          >
            {showLength ? (
              <label className="space-y-1 text-sm">
                <span className="font-medium text-foreground">Length (ft)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={1}
                  max={20}
                  step="any"
                  value={value.lengthFt === "" ? "" : value.lengthFt}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      lengthFt: parseOptionalFt(e.target.value),
                    })
                  }
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
                  placeholder="e.g. 4"
                />
              </label>
            ) : null}
            {showWidth ? (
              <label className="space-y-1 text-sm">
                <span className="font-medium text-foreground">Width (ft)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={1}
                  max={10}
                  step="any"
                  value={value.widthFt === "" ? "" : value.widthFt}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      widthFt: parseOptionalFt(e.target.value),
                    })
                  }
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
                  placeholder="e.g. 2"
                />
              </label>
            ) : null}
            {showHeight ? (
              <label className="space-y-1 text-sm">
                <span className="font-medium text-foreground">Height (ft)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={1}
                  max={10}
                  step="any"
                  value={value.heightFt === "" ? "" : value.heightFt}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      heightFt: parseOptionalFt(e.target.value),
                    })
                  }
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
                  placeholder="e.g. 2.5"
                />
              </label>
            ) : null}
          </div>
          <p className="text-xs text-text-light">
            Optional — skip if you don&apos;t know the size.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Resolve a form value against option pills; non-matches render as unselected. */
export function resolveSelectedSpecOption<T extends string | number>(
  selected: unknown,
  options: readonly T[],
): T | "" {
  if (
    (typeof selected === "string" || typeof selected === "number") &&
    (options as readonly (string | number)[]).includes(selected)
  ) {
    return selected as T;
  }
  return "";
}

/** Shared pill row for furniture / dining-style enum picks. */
export function SpecOptionPills<T extends string | number>({
  options,
  selected,
  onSelect,
  formatLabel,
}: {
  options: readonly T[];
  /**
   * Form value may be a wider union (shared bundle state). Only values in
   * `options` highlight; others show as none selected.
   */
  selected: unknown;
  onSelect: (value: T) => void;
  formatLabel: (value: T) => string;
}) {
  const resolved = resolveSelectedSpecOption(selected, options);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = resolved === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={cn(
              "rounded-lg border px-3 py-2 text-sm font-medium transition",
              isSelected
                ? "border-primary bg-primary-50 text-primary-800"
                : "border-border bg-surface text-foreground hover:border-border-strong",
            )}
            aria-pressed={isSelected}
          >
            {formatLabel(option)}
          </button>
        );
      })}
    </div>
  );
}
